'use es6';

// Math from: https://www.highcharts.com/blog/snippets/alternative-maths-plotting-negative-values-logarithmic-axis/

// A bit confusing but highcharts calls log2lin() on the passed in data
// and lin2log() on the axis tick mark values. So take a vertical bar chart
// for ex. where the yAxis is logarithmic. The y data passes through log2lin
// to get the "real" y values while lin2log() is called on the "real" yAxis
// tick mark values (0, 2, 4, etc..) which returns the values that
// end up getting displayed (1, 100, 10,000, etc...)
// so while visually a logarithmic chart might look like:

// Fig 1:

// 10,000 - 9,000
//        -   |
//    100 -   |        980
//        -   |         |
//      1 - --|---------|----
//
// it is "really" charted like this:

// Fig 2:

//      4 -  3.95 -> (log_10(9000))
//        -   |
//      2 -   |        2.94 -> (log_10(980))
//        -   |         |
//      0 - --|---------|----
//
// But we need to modify these functions so our resulting chart handles
// negatives and is 0-based rather than 1-based as in Fig 1. as explained below

// The log function derived from the curve that intersects (0,0) and (10,1)
// where x is the absolute value of the data point value passed in and y is the
// result. We use this function because the standard logarithmic functinon of
// y = log_10(x) has an undefined output for x = 0. We want output to be 0
// when x = 0 while maintaining the same standard expected output for
// inputs >= 10. In other words we want a log mapping that
// gets us log_10(0) ==> 0 and log_10(10) ==> 1, which is the log function that
// intersects (0,0) and (10,10). This is also just to mirror the custom mappings
// done in the inverse Lin2Log function. The inverse of this function is the
// curve that would be drawn if we replaced x in remapLin2LogResultForZeroBased
// with 10^x which is the inverse of the standard log_10(x) -- this means we
// have the correct mapping functions for lin2log and log2lin.
const remapLog2LinResultForZeroBased = x => {
  const y = Math.log10(x + (10 - x) / 10);
  return y;
};
export const zeroNegativesInclusiveLog2Lin = num => {
  const isNegative = num < 0;
  const adjustedNum = Math.abs(num);
  let result;

  // the logic here is also just accounting for the adjusted Lin2Log function
  // to do the reverse mapping where 10 ==> 10^1 and 0 ==> 10^0
  if (adjustedNum < 10) {
    result = remapLog2LinResultForZeroBased(adjustedNum);
  } else {
    result = Math.log10(adjustedNum);
  }
  return isNegative ? -result : result;
};

// The linear function derived from the line that intersects (1,0) and (10,10)
// where x is the axis label we'll be passing in and y is the remapped axis label.
// We use this function because the traditional logarithmic conversion inputs the
// "real" 0th value (0) and outputs 10^0 == 1, but to get a 0-based chart we
// want the output to be 0 while maintaining the same standard behavior for the
// following values (1, 2, 3, etc...). In other words, we want a linear mapping
// that gets us 10^0 ==> 0 and 10^1 ==> 10, which is the function that intersects
// (1,0) and (10,10). If we replace x with what really gets passed in which is
// 10^n where n is the original value passed in, we'll get the inverse of the
// function in remapLog2LinResultForZeroBased
const remapLin2LogResultForZeroBased = x => {
  const y = 10 * (x - 1) / (10 - 1);
  return y;
};
export const zeroNegativesInclusiveLin2Log = num => {
  const isNegative = num < 0;
  let result = Math.pow(10, Math.abs(num));

  // the logic here is just saying we need to remap "num" values between [0-1)
  // since 10^0 = 1 and 10^1 = 10. Passing in 0 should get us 0 but passing in
  // 10 should still get us 10.
  if (result < 10) {
    result = remapLin2LogResultForZeroBased(result);
  }
  return isNegative ? -result : result;
};